// src/pages/BlogIndex.jsx
// Route: /blog
// Lists all blog posts with search, category filter, and tag filter

import React, { useState, useMemo, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Search, Clock, ChevronRight, TrendingUp } from 'lucide-react';
import {
  BLOG_POSTS, BLOG_CATEGORIES, BLOG_TAGS,
  searchPosts, formatDate
} from '../data/blogPosts';

// ── SEO ───────────────────────────────────────────────────────────────────────
function setMeta(name, content, attr = 'name') {
  let el = document.querySelector(`meta[${attr}="${name}"]`);
  if (!el) { el = document.createElement('meta'); el.setAttribute(attr, name); document.head.appendChild(el); }
  el.setAttribute('content', content);
}

// ── Navbar (reuse same style as rest of site) ─────────────────────────────────
function Navbar() {
  return (
    <nav className="bg-white shadow-md sticky top-0 z-50">
      <div className="max-w-6xl mx-auto px-6 py-4">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <Link to="/" className="text-2xl font-bold text-yellow-600 hover:text-yellow-700 transition-colors">
            LivePriceForGold.com
          </Link>
          <div className="flex items-center gap-8">
            <Link to="/"                className="text-sm font-medium text-gray-500 hover:text-yellow-600 transition-colors">Home</Link>
            <a href="/calculator.html"  className="text-sm font-medium text-gray-500 hover:text-yellow-600 transition-colors">Gold Calculator</a>
            <Link to="/blog"            className="text-sm font-medium text-yellow-600 font-semibold">Blog</Link>
            <a href="/about.html"       className="text-sm font-medium text-gray-500 hover:text-yellow-600 transition-colors">About Us</a>
            <a href="/contact.html"     className="text-sm font-medium text-gray-500 hover:text-yellow-600 transition-colors">Contact</a>
          </div>
        </div>
      </div>
    </nav>
  );
}

function Footer() {
  return (
    <div className="bg-gray-800 text-white py-8 px-6 mt-16">
      <div className="max-w-6xl mx-auto text-center">
        <p className="text-xs opacity-60 mb-2">Disclaimer: Articles are for informational purposes only and do not constitute financial advice.</p>
        <p className="text-xs opacity-60">© {new Date().getFullYear()} LivePriceForGold.com. All rights reserved.</p>
      </div>
    </div>
  );
}

// ── Post Card ─────────────────────────────────────────────────────────────────
function PostCard({ post }) {
  return (
    <Link
      to={`/blog/${post.slug}`}
      className="group bg-white rounded-xl shadow-md hover:shadow-xl transition-all duration-300 overflow-hidden flex flex-col"
    >
      {/* Category banner */}
      <div className="bg-gradient-to-r from-yellow-500 to-amber-500 px-4 py-2 flex items-center justify-between">
        <span className="text-white text-xs font-bold uppercase tracking-wider">{post.category}</span>
        <span className="text-yellow-100 text-xs flex items-center gap-1">
          <Clock className="w-3 h-3" />{post.readTime} min read
        </span>
      </div>

      <div className="p-5 flex flex-col flex-1">
        <h2 className="font-bold text-gray-800 text-lg leading-snug mb-2 group-hover:text-yellow-600 transition-colors line-clamp-2">
          {post.title}
        </h2>
        <p className="text-gray-500 text-sm leading-relaxed mb-4 flex-1 line-clamp-3">
          {post.excerpt}
        </p>

        {/* Tags */}
        <div className="flex flex-wrap gap-1.5 mb-4">
          {post.tags.slice(0, 3).map(tag => (
            <span key={tag} className="text-xs bg-yellow-50 text-yellow-700 px-2 py-0.5 rounded-full border border-yellow-200">
              {tag}
            </span>
          ))}
        </div>

        <div className="flex items-center justify-between text-xs text-gray-400 border-t border-gray-100 pt-3">
          <span>{formatDate(post.date)}</span>
          <span className="flex items-center gap-1 text-yellow-600 font-semibold group-hover:gap-2 transition-all">
            Read more <ChevronRight className="w-3 h-3" />
          </span>
        </div>
      </div>
    </Link>
  );
}

// ── Main BlogIndex ─────────────────────────────────────────────────────────────
export default function BlogIndex() {
  const [query, setQuery]             = useState('');
  const [activeCategory, setCategory] = useState('All');
  const [activeTag, setTag]           = useState('');

  useEffect(() => {
    document.title = 'Gold Blog — News, Analysis & Guides | LivePriceForGold.com';
    setMeta('description', 'Stay up to date with gold market news, price forecasts, investment guides, and educational articles. Updated weekly by the LivePriceForGold.com team.');
    setMeta('og:title', 'Gold Blog | LivePriceForGold.com', 'property');
    let canonical = document.querySelector('link[rel="canonical"]');
    if (!canonical) { canonical = document.createElement('link'); canonical.rel = 'canonical'; document.head.appendChild(canonical); }
    canonical.href = 'https://livepriceforgold.com/blog';
  }, []);

  const filtered = useMemo(() => {
    let posts = query ? searchPosts(query) : BLOG_POSTS;
    if (activeCategory !== 'All') posts = posts.filter(p => p.category === activeCategory);
    if (activeTag) posts = posts.filter(p => p.tags.includes(activeTag));
    return posts;
  }, [query, activeCategory, activeTag]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-yellow-50 via-amber-50 to-orange-50">
      <Navbar />

      {/* Hero */}
      <div className="bg-gradient-to-r from-yellow-600 to-amber-600 text-white py-14 px-6">
        <div className="max-w-6xl mx-auto">
          <div className="flex items-center gap-3 mb-3">
            <TrendingUp className="w-8 h-8" />
            <h1 className="text-4xl font-bold">Gold Market Blog</h1>
          </div>
          <p className="text-yellow-100 text-lg max-w-xl">
            Weekly news, price forecasts, buying guides, and market analysis — all in one place.
          </p>
          <p className="text-yellow-200 text-sm mt-2">{BLOG_POSTS.length} articles published</p>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-6 py-8">
        <div className="flex flex-col lg:flex-row gap-8">

          {/* ── Left: post grid ── */}
          <div className="flex-1 min-w-0">

            {/* Search bar */}
            <div className="relative mb-6">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 w-5 h-5" />
              <input
                type="text"
                placeholder="Search articles…"
                value={query}
                onChange={e => { setQuery(e.target.value); setCategory('All'); setTag(''); }}
                className="w-full pl-12 pr-4 py-3 bg-white border border-gray-200 rounded-xl shadow-sm focus:outline-none focus:ring-2 focus:ring-yellow-400 text-sm"
              />
            </div>

            {/* Active filters */}
            {(query || activeCategory !== 'All' || activeTag) && (
              <div className="flex items-center gap-2 mb-4 flex-wrap">
                <span className="text-sm text-gray-500">{filtered.length} result{filtered.length !== 1 ? 's' : ''}</span>
                <button onClick={() => { setQuery(''); setCategory('All'); setTag(''); }}
                  className="text-xs text-yellow-600 underline hover:no-underline">Clear filters</button>
              </div>
            )}

            {/* Post grid */}
            {filtered.length > 0 ? (
              <div className="grid sm:grid-cols-2 gap-6">
                {filtered.map(post => <PostCard key={post.slug} post={post} />)}
              </div>
            ) : (
              <div className="text-center py-16 text-gray-400">
                <p className="text-lg font-medium">No articles found</p>
                <p className="text-sm mt-1">Try a different search term</p>
              </div>
            )}
          </div>

          {/* ── Right: sidebar ── */}
          <div className="lg:w-64 shrink-0 space-y-6">

            {/* Categories */}
            <div className="bg-white rounded-xl shadow-md p-5">
              <h3 className="font-bold text-gray-800 mb-3 text-sm uppercase tracking-wider">Categories</h3>
              <ul className="space-y-1">
                {['All', ...BLOG_CATEGORIES].map(cat => (
                  <li key={cat}>
                    <button
                      onClick={() => { setCategory(cat); setTag(''); setQuery(''); }}
                      className={`w-full text-left text-sm px-3 py-2 rounded-lg transition-colors ${
                        activeCategory === cat
                          ? 'bg-yellow-100 text-yellow-700 font-semibold'
                          : 'text-gray-600 hover:bg-gray-50'
                      }`}
                    >
                      {cat}
                      <span className="float-right text-gray-400 text-xs">
                        {cat === 'All' ? BLOG_POSTS.length : BLOG_POSTS.filter(p => p.category === cat).length}
                      </span>
                    </button>
                  </li>
                ))}
              </ul>
            </div>

            {/* Popular Tags */}
            <div className="bg-white rounded-xl shadow-md p-5">
              <h3 className="font-bold text-gray-800 mb-3 text-sm uppercase tracking-wider">Popular Tags</h3>
              <div className="flex flex-wrap gap-2">
                {BLOG_TAGS.slice(0, 15).map(tag => (
                  <button
                    key={tag}
                    onClick={() => { setTag(tag === activeTag ? '' : tag); setQuery(''); setCategory('All'); }}
                    className={`text-xs px-3 py-1.5 rounded-full border transition-colors ${
                      activeTag === tag
                        ? 'bg-yellow-500 text-white border-yellow-500'
                        : 'bg-white text-gray-600 border-gray-200 hover:border-yellow-400 hover:text-yellow-600'
                    }`}
                  >
                    {tag}
                  </button>
                ))}
              </div>
            </div>

            {/* Gold Price Link */}
            <div className="bg-gradient-to-br from-yellow-500 to-amber-600 rounded-xl p-5 text-white">
              <h3 className="font-bold mb-2">Live Gold Prices</h3>
              <p className="text-yellow-100 text-xs mb-3">Check real-time gold rates in 140+ currencies.</p>
              <Link to="/" className="block text-center bg-white text-yellow-600 font-bold text-sm py-2 rounded-lg hover:bg-yellow-50 transition-colors">
                View All Rates →
              </Link>
            </div>

          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
}
